import { ChartFrequency, IComposableChartsWidget } from 'wisestep-api';

import { generalTimePeriodList } from '../../../../analyticsPage/helper/timePeriodList';
import JobsBarChartPostgre from '../../../BaseCharts/jobs/postgre/JobsBarChartPostgre';
import JobsPostedMetricChartPostgre from '../../../BaseCharts/jobs/postgre/JobsPostedMetricChartPostgre';
import JobsTablePostgre from '../../../BaseCharts/jobs/postgre/JobsTablePostgre';
import { ChartScope } from '../../../types';
import { getChartFreqForTimePeriod, JobsWidget } from '../../helper';

export const jobsTimePeriodList = generalTimePeriodList.filter(
  (time) => !['1H', '8H'].includes(time.id)
);

const getJobsChartWidgetByTimeAndScope = (
  timePeriodId: string,
  scope: ChartScope,
  frequency = getChartFreqForTimePeriod[timePeriodId]
): Record<JobsWidget, IComposableChartsWidget> => {
  const defaultQuery = {
    category: 'job',
    filters: [],
    type: 'count-metric',
    range: timePeriodId,
    scope,
    frequency,
  };
  const freqInRegularCase = frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();

  const widgetConfig = {
    [JobsWidget.METRIC_TOTAL]: {
      name: 'Jobs Posted',
      filters: {},
      category: 'jobs',
      type: `metric_jobs_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsPostedMetricChartPostgre
          query={{ ...defaultQuery, type: 'count-metric' }}
          title="Jobs Posted"
          widgetName={JobsWidget.METRIC_TOTAL}
        />
      ),
    },
    [JobsWidget.METRIC_TYPE_WISE]: {
      name: 'Jobs By Type',
      filters: {},
      category: 'jobs',
      type: `metric_jobs_type_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsPostedMetricChartPostgre
          query={{ ...defaultQuery, type: 'type-wise-count-metric' }}
          title="Jobs By Type"
          widgetName={JobsWidget.METRIC_TYPE_WISE}
        />
      ),
    },
    [JobsWidget.METRIC_STATUS_WISE]: {
      name: 'Jobs By Status',
      filters: {},
      category: 'jobs',
      type: `metric_jobs_status_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsPostedMetricChartPostgre
          query={{ ...defaultQuery, type: 'status-wise-count-metric' }}
          title="Jobs By Status"
          widgetName={JobsWidget.METRIC_STATUS_WISE}
        />
      ),
    },
    [JobsWidget.TABLE_ACCOUNT_MANAGER]: {
      name: 'Jobs by Account Managers',
      filters: {},
      category: 'jobs',
      type: `table_jobs_account_manager_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'count-per-account-manager-table' }}
          title="Jobs by Account Managers"
          widgetName={JobsWidget.TABLE_ACCOUNT_MANAGER}
        />
      ),
    },
    [JobsWidget.TABLE_CLIENT]: {
      name: 'Jobs by Clients',
      filters: {},
      category: 'jobs',
      type: `table_jobsposted_clients_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'count-per-client-table' }}
          title="Jobs by Clients"
          widgetName={JobsWidget.TABLE_CLIENT}
        />
      ),
    },
    [JobsWidget.TABLE_CLIENT_SPOC_STATUS_WISE]: {
      name: 'Jobs Status By Client Contact',
      filters: {},
      category: 'jobs',
      type: `table_jobs_status_spoc_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'status-wise-count-per-client-contact-table' }}
          title="Jobs Status By Client Contact"
          widgetName={JobsWidget.TABLE_CLIENT_SPOC_STATUS_WISE}
        />
      ),
    },
    [JobsWidget.TABLE_CLIENT_SPOC_TYPE_WISE]: {
      name: 'Job Type By Client Contact',
      filters: {},
      category: 'jobs',
      type: `table_jobs_type_spoc_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'type-wise-count-per-client-contact-table' }}
          title="Job Type By Client Contact"
          widgetName={JobsWidget.TABLE_CLIENT_SPOC_TYPE_WISE}
        />
      ),
    },
    [JobsWidget.TABLE_CLIENT_STATUS_WISE]: {
      name: 'Jobs Status By Client',
      filters: {},
      category: 'jobs',
      type: `table_jobs_status_client_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'status-wise-count-per-client-table' }}
          title="Jobs Status By Client"
          widgetName={JobsWidget.TABLE_CLIENT_STATUS_WISE}
        />
      ),
    },
    [JobsWidget.TABLE_CLIENT_TYPE_WISE]: {
      name: 'Jobs Type By Client',
      filters: {},
      category: 'jobs',
      type: `table_jobs_type_client_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'type-wise-count-per-client-table' }}
          title="Jobs Type By Client"
          widgetName={JobsWidget.TABLE_CLIENT_TYPE_WISE}
        />
      ),
    },
    [JobsWidget.TABLE_CLIENT_SPOC]: {
      name: 'Jobs By Client Contacts',
      filters: {},
      category: 'jobs',
      type: `table_jobs_client_and_client_spoc_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'count-per-client-contact-table' }}
          title="Jobs By Client Contacts"
          widgetName={JobsWidget.TABLE_CLIENT_SPOC}
        />
      ),
    },
    [JobsWidget.METRIC_ACCEPTED]: {
      name: 'Jobs Accepted',
      filters: {},
      category: 'jobs',
      type: `metric_jobs_accepted_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsPostedMetricChartPostgre
          query={{ ...defaultQuery, type: 'accepted-metric' }}
          title="Jobs Accepted"
          widgetName={JobsWidget.METRIC_ACCEPTED}
        />
      ),
    },
    [JobsWidget.METRIC_ACCEPTED_NO_SUBMISSION]: {
      name: 'Jobs Accepted With No Submissions',
      filters: {},
      category: 'jobs',
      type: `metric_jobs_accepted_no_submission_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsPostedMetricChartPostgre
          query={{ ...defaultQuery, type: 'accepted-no-submission-metric' }}
          title="Jobs Accepted With No Submissions"
          widgetName={JobsWidget.METRIC_ACCEPTED_NO_SUBMISSION}
        />
      ),
    },
    [JobsWidget.TABLE_ACCEPTED_NO_SUBMISSION]: {
      name: 'Jobs Accepted With No Submissions',
      filters: {},
      category: 'jobs',
      type: `table_jobs_no_submissions_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'accepted-no-submission-table' }}
          title="Jobs Accepted With No Submissions"
          widgetName={JobsWidget.TABLE_ACCEPTED_NO_SUBMISSION}
        />
      ),
    },
    [JobsWidget.METRIC_ACCEPTED_NO_MAPPING]: {
      name: 'Jobs Accepted With No Mappings',
      filters: {},
      category: 'jobs',
      type: `metric_jobs_accepted_no_mapping_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsPostedMetricChartPostgre
          query={{ ...defaultQuery, type: 'accepted-no-mapping-metric' }}
          title="Jobs Accepted With No Mappings"
          widgetName={JobsWidget.METRIC_ACCEPTED_NO_MAPPING}
        />
      ),
    },
    [JobsWidget.TABLE_ACCEPTED_NO_MAPPING]: {
      name: 'Jobs Accepted With No Mappings',
      filters: {},
      category: 'jobs',
      type: `table_jobs_no_mappings_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'accepted-no-mapping-table' }}
          title="Jobs Accepted With No Mappings"
          widgetName={JobsWidget.TABLE_ACCEPTED_NO_MAPPING}
        />
      ),
    },
    [JobsWidget.TABLE_SUBMISSION_COUNT]: {
      name: 'Jobs by Submission Count',
      filters: {},
      category: 'jobs',
      type: `table_jobs_submission_count_${timePeriodId}_${scope}_postgre`,
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'submission-count-table' }}
          title="Jobs by Submission Count"
          widgetName={JobsWidget.TABLE_SUBMISSION_COUNT}
        />
      ),
    },
    [JobsWidget.BAR_CHART_TOTAL]: {
      name: `Jobs Posted - ${freqInRegularCase}`,
      filters: {},
      category: 'jobs',
      type: `barchart_jobs_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <JobsBarChartPostgre
          query={{ ...defaultQuery, type: 'count-bar-chart' }}
          title={`Jobs Posted - ${freqInRegularCase}`}
          widgetName={JobsWidget.BAR_CHART_TOTAL}
        />
      ),
    },
    [JobsWidget.STACKED_BAR_CHART_CLIENT]: {
      name: `Jobs By Client - ${freqInRegularCase}`,
      filters: {},
      category: 'jobs',
      type: `stackedbarchart_jobs_client_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <JobsBarChartPostgre
          query={{ ...defaultQuery, type: 'count-per-client-stackedbar-chart' }}
          title={`Jobs By Client - ${freqInRegularCase}`}
          widgetName={JobsWidget.STACKED_BAR_CHART_CLIENT}
        />
      ),
    },
    [JobsWidget.STACKED_BAR_CHART_CLIENT_CONTACT]: {
      name: `Jobs By Client Contacts - ${freqInRegularCase}`,
      filters: {},
      category: 'jobs',
      type: `stackedbarchart_jobs_client_contact_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <JobsBarChartPostgre
          query={{ ...defaultQuery, type: 'count-per-client-contact-stackedbar-chart' }}
          title={`Jobs By Client Contacts - ${freqInRegularCase}`}
          widgetName={JobsWidget.STACKED_BAR_CHART_CLIENT_CONTACT}
        />
      ),
    },
    [JobsWidget.STACKED_BAR_CHART_STATUS_WISE]: {
      name: `Jobs By Status - ${freqInRegularCase}`,
      filters: {},
      category: 'jobs',
      type: `stackedbarchart_jobs_status_wise_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <JobsBarChartPostgre
          query={{ ...defaultQuery, type: 'status-wise-count-stackedbar-chart' }}
          title={`Jobs By Status - ${freqInRegularCase}`}
          widgetName={JobsWidget.STACKED_BAR_CHART_STATUS_WISE}
        />
      ),
    },
    [JobsWidget.STACKED_BAR_CHART_TYPE_WISE]: {
      name: `Jobs By Type - ${freqInRegularCase}`,
      filters: {},
      category: 'jobs',
      type: `stackedbarchart_jobs_type_wise_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <JobsBarChartPostgre
          query={{ ...defaultQuery, type: 'type-wise-count-stackedbar-chart' }}
          title={`Jobs By Type - ${freqInRegularCase}`}
          widgetName={JobsWidget.STACKED_BAR_CHART_TYPE_WISE}
        />
      ),
    },
    [JobsWidget.TABLE_JOB_TIME_TO_SERVICE]: {
      name: 'Job demand service',
      filters: {},
      type: `table_jobs_job_service_${timePeriodId}_${scope}_postgre`,
      category: 'jobs',
      component: (
        <JobsTablePostgre
          query={{
            ...defaultQuery,
            type: 'TABLE_JOB_TIME_TO_SERVICE',
          }}
          title="Job demand service"
          widgetName={JobsWidget.TABLE_JOB_TIME_TO_SERVICE}
        />
      ),
    },
  };

  return widgetConfig;
};

export const jobsChartsKeyPostgre = (
  timePeriodId: string,
  scope: ChartScope
): IComposableChartsWidget[] => {
  const defaultWidgetList = Object.values(getJobsChartWidgetByTimeAndScope(timePeriodId, scope));
  const weeklyCharts = getJobsChartWidgetByTimeAndScope(timePeriodId, scope, ChartFrequency.WEEKLY);
  const dailyCharts = getJobsChartWidgetByTimeAndScope(timePeriodId, scope, ChartFrequency.DAILY);
  const extraWidgetList = {
    '30D': [
      weeklyCharts[JobsWidget.BAR_CHART_TOTAL],
      weeklyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT],
      weeklyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT_CONTACT],
    ],
    LAST_QUARTER: [
      dailyCharts[JobsWidget.BAR_CHART_TOTAL],
      weeklyCharts[JobsWidget.BAR_CHART_TOTAL],
      dailyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT],
      weeklyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT],
      dailyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT_CONTACT],
      weeklyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT_CONTACT],
    ],
    CURRENT_QUARTER: [
      dailyCharts[JobsWidget.BAR_CHART_TOTAL],
      weeklyCharts[JobsWidget.BAR_CHART_TOTAL],
      dailyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT],
      weeklyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT],
      dailyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT_CONTACT],
      weeklyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT_CONTACT],
    ],
    YEAR_TILL_DATE: [
      weeklyCharts[JobsWidget.BAR_CHART_TOTAL],
      weeklyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT],
      weeklyCharts[JobsWidget.STACKED_BAR_CHART_CLIENT_CONTACT],
    ],
  };

  return [...defaultWidgetList, ...(extraWidgetList[timePeriodId] ?? [])];
};

export const postgreJobsDashboardKeysObjectList = jobsTimePeriodList.flatMap((timePeriod) => [
  ...jobsChartsKeyPostgre(timePeriod.id, ChartScope.WORKSPACE),
  ...jobsChartsKeyPostgre(timePeriod.id, ChartScope.TEAMS),
  ...jobsChartsKeyPostgre(timePeriod.id, ChartScope.LOGGED_IN_USER),
]);
